/**
 * Stable identifier for a managed CLI runtime.
 *
 * The API Manager is registry-driven: the Daemon may advertise a CLI before
 * this client release knows its friendly name.  Keep the wire identity opaque
 * at the SDK boundary so a new Adapter is rendered read-only instead of being
 * silently discarded by a legacy decoder.  Built-in IDs are exported for
 * backwards-compatible defaults and labels.
 */
export type ApiCliId = string;

export const BUILTIN_API_CLI_IDS = ["codex", "opencode", "claude", "pi", "grok"] as const;

export interface ApiCliProtocolSupport {
  cli: ApiCliId;
  direct: boolean;
  proxy: boolean;
  noteCode: string;
}

/**
 * Adapter-owned CLI metadata returned by the API Daemon.  Keeping this
 * descriptor separate from persisted settings means a newly installed CLI
 * can appear in API Manager before its first binding has been saved.
 */
export interface ApiCliDescriptor {
  cli: ApiCliId;
  displayName: string;
  configSupported: boolean;
  proxyRoutes: string[];
}

export interface ApiProtocolDescriptor {
  protocolId: string;
  family: string;
  authKind: string;
  defaultBaseUrl?: string;
  supportsModelListing: boolean;
  cliSupport: ApiCliProtocolSupport[];
}

export interface ApiProviderRecord {
  providerId: string;
  name: string;
  protocolId: string;
  baseUrl: string;
  hasApiKey: boolean;
  defaultModel?: string;
  homepageUrl?: string;
  notes?: string;
  metadata: Record<string, string>;
  models: string[];
  lastTestStatus: "unknown" | "available" | "unavailable";
  lastTestError?: string;
  lastTestAt?: string;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface SaveApiProviderInput {
  providerId?: string;
  name: string;
  protocolId: string;
  baseUrl: string;
  apiKey?: string;
  clearApiKey?: boolean;
  defaultModel?: string;
  homepageUrl?: string;
  notes?: string;
  metadata?: Record<string, string>;
  expectedRevision?: number;
}

export interface ApiProviderTestResult {
  providerId: string;
  ok: boolean;
  statusCode?: number;
  latencyMs: number;
  message: string;
  checkedAt: string;
}

export interface ApiProtocolDetection {
  protocolId: string;
  confidence: "high" | "medium";
  evidenceCode: string;
}

export interface ApiProxySettings {
  enabled: boolean;
  running: boolean;
  restoreError?: string;
  port: number;
  revision: number;
  updatedAt: string;
  apiKey?: string;
  endpoints: Record<ApiCliId, ApiProxyEndpoint[]>;
}

export interface ApiProxyEndpoint {
  route: "responses" | "chat" | "anthropic";
  url: string;
}

export interface ApiProviderCredential {
  providerId: string;
  apiKey?: string;
  maskedApiKey?: string;
}

export interface ApiCliBinding {
  bindingId: string;
  cli: ApiCliId;
  providerId: string;
  priority: number;
  enabled: boolean;
  healthStatus: "unknown" | "available" | "degraded" | "unavailable";
  consecutiveFailures: number;
  degradedSince?: string;
  lastAttemptAt?: string;
  lastSuccessAt?: string;
  lastFailureAt?: string;
  lastError?: string;
  revision: number;
}

export interface ApiCliSettings {
  cli: ApiCliId;
  useProxy: boolean;
  revision: number;
  updatedAt: string;
  configPath: string;
  bindings: ApiCliBinding[];
}

export interface ApiCurrentCliConfig {
  cli: ApiCliId;
  path: string;
  status: "configured" | "not_configured" | "unreadable";
  providerName?: string;
  protocolId?: string;
  baseUrl?: string;
  defaultModel?: string;
  hasApiKey: boolean;
  matchedProviderId?: string;
  usesOrniProxy: boolean;
  error?: string;
  checkedAt: string;
}

export interface ApiImportCurrentConfigResult {
  provider: ApiProviderRecord;
  addedToRouting: boolean;
}

export interface SaveApiCliSettingsInput {
  useProxy: boolean;
  bindings: { providerId: string; enabled: boolean }[];
  expectedRevision?: number;
}

export interface ApiConfigPreview {
  cli: ApiCliId;
  path: string;
  before: string;
  after: string;
  currentSha256: string;
  containsSecret: boolean;
  /** Remote Cloud receipts redact file contents before they leave the device. */
  contentRedacted?: boolean;
  warnings: string[];
}

export interface ApiConfigApplyResult {
  cli: ApiCliId;
  path: string;
  backupPath?: string;
  appliedAt: string;
}

export interface ApiRequestLog {
  logId: string;
  cli: ApiCliId;
  providerId?: string;
  protocolId?: string;
  method: string;
  path: string;
  requestBytes: number;
  userAgent?: string;
  model?: string;
  statusCode?: number;
  latencyMs: number;
  outcome: string;
  error?: string;
  createdAt: string;
}

export interface ApiRequestLogPage {
  logs: ApiRequestLog[];
  retainedMinutes: number;
}

export interface ApiManagerSnapshot {
  daemonVersion: string;
  /** Optional for rolling upgrades; older API Daemons omit this field. */
  cliDescriptors?: ApiCliDescriptor[];
  protocols: ApiProtocolDescriptor[];
  providers: ApiProviderRecord[];
  proxy: ApiProxySettings;
  cliSettings: ApiCliSettings[];
}
